import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Eye, EyeOff, LocateFixed, MapPin, MapPinOff, Minus, MousePointer2, Plus } from 'lucide-react';

import { Button } from '../ui/button';
import { pinToMapPoint, resolvePinColor } from '../../lib/mapPins';
import {
  axialDisk,
  axialLine,
  axialToWorld,
  calibrateGridFromTwoPoints,
  clamp,
  formatDistance,
  formatHexId,
  getHexCorners,
  getMapAxialBounds,
  getScaleBar,
  getVisibleAxialRange,
  hexKey,
  isPointInMap,
  parseHexKey,
  worldToAxial,
} from './hexGridMath';
import { networkEdgesBetween, networkTypeKey } from './mapFeatures';

const MIN_SCREEN_GRID_SIZE = 3;
const MAX_VISIBLE_HEXES = 30000;

const canvasCopy = {
  fr: {
    fit: 'Vue d’ensemble (F)',
    zoomIn: 'Zoom avant',
    zoomOut: 'Zoom arrière',
    hideGrid: 'Masquer la grille (G)',
    showGrid: 'Afficher la grille (G)',
    gridHint: 'Alt + molette : taille · Flèches : décalage · G : grille · F : vue générale',
    gridPoint1: 'Cliquez le centre du premier hexagone',
    gridPoint2: 'Cliquez le centre d’un hexagone voisin',
    scalePoint1: 'Cliquez le début de la distance connue',
    scalePoint2: 'Cliquez la fin de la distance connue',
    dense: 'Grille masquée à ce niveau de zoom',
    imageError: "L’image de la carte n’a pas pu être chargée.",
    sampleTerrain: 'Cliquez un hexagone représentatif',
    paintTerrain: 'Maintenez le clic gauche et déplacez la souris pour peindre',
    selectTerrain: 'Cliquez les hexagones à ajouter ou retirer de la sélection',
    routeStart: 'Cliquez l’hexagone de départ',
    routeDestination: "Cliquez l’hexagone d’arrivée",
    routeWaypoint: 'Cliquez un passage obligé',
    routeEdit: 'Cliquez un hexagone du tracé pour forcer un détour',
    drawNetwork: 'Glissez d’un hexagone à l’autre pour tracer le réseau',
    eraseNetwork: 'Glissez sur un réseau pour effacer ses segments',
    paintCondition: 'Cliquez un hexagone pour placer cet obstacle',
    eraseCondition: 'Cliquez un obstacle pour le retirer',
    hidePins: 'Masquer le calque des pins',
    showPins: 'Afficher le calque des pins',
    noPins: 'Aucun pin sur cette carte',
  },
  en: {
    fit: 'Fit to view (F)',
    zoomIn: 'Zoom in',
    zoomOut: 'Zoom out',
    hideGrid: 'Hide grid (G)',
    showGrid: 'Show grid (G)',
    gridHint: 'Alt + wheel: size · Arrows: offset · G: grid · F: fit',
    gridPoint1: 'Click the center of the first hex',
    gridPoint2: 'Click the center of a neighboring hex',
    scalePoint1: 'Click the start of the known distance',
    scalePoint2: 'Click the end of the known distance',
    dense: 'Grid hidden at this zoom level',
    imageError: 'The map image could not be loaded.',
    sampleTerrain: 'Click a representative hex',
    paintTerrain: 'Hold the left mouse button and drag to paint',
    selectTerrain: 'Click hexes to add or remove them from the selection',
    routeStart: 'Click the start hex',
    routeDestination: 'Click the destination hex',
    routeWaypoint: 'Click a required waypoint',
    routeEdit: 'Click a route hex to force a detour',
    drawNetwork: 'Drag from hex to hex to draw the network',
    eraseNetwork: 'Drag over a network to erase its segments',
    paintCondition: 'Click a hex to place this obstacle',
    eraseCondition: 'Click an obstacle to remove it',
    hidePins: 'Hide pin layer',
    showPins: 'Show pin layer',
    noPins: 'No pins on this map',
  },
};

export function TravelMapCanvas({
  map,
  calibration,
  language = 'fr',
  distanceUnit = 'km',
  interactionMode = 'navigate',
  onGridChange,
  onGridCalibrated,
  onScaleSegmentMeasured,
  onInteractionComplete,
  onImageDimensions,
  terrainAssignments = {},
  terrainPalette = {},
  terrainOverlayEnabled = true,
  terrainOverlayOpacity = 0.34,
  terrainBrushColor = '#22D3EE',
  selectedTerrainHexes = new Set(),
  brushRadius = 0,
  onTerrainSample,
  onTerrainStrokeStart,
  onTerrainPaint,
  onTerrainStrokeEnd,
  onToggleTerrainHex,
  networkSegments = {},
  networkPalette = {},
  networkOverlayEnabled = true,
  conditionAssignments = {},
  conditionPalette = {},
  conditionOverlayEnabled = true,
  onNetworkStrokeStart,
  onNetworkPaint,
  onNetworkStrokeEnd,
  onConditionPaint,
  routeVariants = [],
  selectedRouteProfileId,
  routePoints,
  avoidedRouteHexKeys = [],
  focusedRouteHexKey,
  onRouteHexPick,
  routeProfileColors = {},
  pins = [],
  pinsVisible = true,
  onPinsVisibleChange,
  partyAnimation,
  fitViewKey,
  immersive = false,
}) {
  const text = canvasCopy[language] || canvasCopy.fr;
  const containerRef = useRef(null);
  const canvasRef = useRef(null);
  const terrainStrokeCanvasRef = useRef(null);
  const terrainPreviewCanvasRef = useRef(null);
  const dragRef = useRef(null);
  const terrainStrokeRef = useRef(null);
  const featureStrokeRef = useRef(null);
  const hasFitRef = useRef(false);
  const [viewport, setViewport] = useState({ width: 1, height: 1 });
  const [view, setView] = useState({ x: 0, y: 0, scale: 1 });
  const [measurementPoints, setMeasurementPoints] = useState([]);
  const [hoveredTerrainAxial, setHoveredTerrainAxial] = useState(null);
  const [imageError, setImageError] = useState(false);
  const [gridTooDense, setGridTooDense] = useState(false);

  const grid = calibration.grid;
  const imageWidth = calibration.imageWidth;
  const imageHeight = calibration.imageHeight;

  const fitView = useCallback((width = imageWidth, height = imageHeight, cover = immersive) => {
    const container = containerRef.current;
    if (!container || !width || !height) return;
    const rect = container.getBoundingClientRect();
    const scale = cover
      ? Math.max(rect.width / width, rect.height / height) * 1.01
      : Math.min(rect.width / width, rect.height / height) * 0.94;
    setView({
      scale,
      x: (rect.width - width * scale) / 2,
      y: (rect.height - height * scale) / 2,
    });
    hasFitRef.current = true;
  }, [imageHeight, imageWidth, immersive]);

  useEffect(() => {
    hasFitRef.current = false;
    setMeasurementPoints([]);
    setImageError(false);
  }, [map.id]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return undefined;
    const observer = new ResizeObserver(([entry]) => {
      const { width, height } = entry.contentRect;
      setViewport({ width: Math.max(1, width), height: Math.max(1, height) });
    });
    observer.observe(container);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!hasFitRef.current && viewport.width > 1 && imageWidth && imageHeight) fitView();
  }, [fitView, imageHeight, imageWidth, viewport]);

  useEffect(() => {
    if (fitViewKey === undefined) return undefined;
    const timeoutId = window.setTimeout(() => fitView(), 320);
    return () => window.clearTimeout(timeoutId);
  }, [fitView, fitViewKey]);

  useEffect(() => {
    setMeasurementPoints([]);
    setHoveredTerrainAxial(null);
  }, [interactionMode]);

  const mapBounds = useMemo(
    () => getMapAxialBounds(grid, imageWidth, imageHeight),
    [grid, imageHeight, imageWidth],
  );

  const prepareOverlayContext = useCallback((canvas, clear = false) => {
    if (!canvas) return null;
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const pixelWidth = Math.round(viewport.width * dpr);
    const pixelHeight = Math.round(viewport.height * dpr);
    if (canvas.width !== pixelWidth || canvas.height !== pixelHeight) {
      canvas.width = pixelWidth;
      canvas.height = pixelHeight;
      canvas.style.width = `${viewport.width}px`;
      canvas.style.height = `${viewport.height}px`;
    }
    const context = canvas.getContext('2d');
    context.setTransform(dpr, 0, 0, dpr, 0, 0);
    if (clear) context.clearRect(0, 0, viewport.width, viewport.height);
    return context;
  }, [viewport]);

  const drawTerrainCells = useCallback((canvas, cells, { clear = false, previewCenter = null } = {}) => {
    const context = prepareOverlayContext(canvas, clear);
    if (!context || !cells?.length) return;
    const toScreen = (point) => ({
      x: point.x * view.scale + view.x,
      y: point.y * view.scale + view.y,
    });
    cells.forEach((cell) => {
      const axial = typeof cell === 'string' ? parseHexKey(cell) : cell;
      const center = axialToWorld(axial.q, axial.r, grid);
      if (!isPointInMap(center, imageWidth, imageHeight, grid.sizePx)) return;
      const corners = getHexCorners(center, grid).map(toScreen);
      context.beginPath();
      context.moveTo(corners[0].x, corners[0].y);
      corners.slice(1).forEach((corner) => context.lineTo(corner.x, corner.y));
      context.closePath();
      const isCenter = previewCenter?.q === axial.q && previewCenter?.r === axial.r;
      context.globalAlpha = previewCenter ? (isCenter ? 0.42 : 0.28) : 0.46;
      context.fillStyle = terrainBrushColor;
      context.fill();
      context.globalAlpha = 0.95;
      context.strokeStyle = isCenter ? '#FFFFFF' : terrainBrushColor;
      context.lineWidth = isCenter ? 2.5 : 1.5;
      context.stroke();
    });
    context.globalAlpha = 1;
  }, [grid, imageHeight, imageWidth, prepareOverlayContext, terrainBrushColor, view]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    canvas.width = Math.round(viewport.width * dpr);
    canvas.height = Math.round(viewport.height * dpr);
    canvas.style.width = `${viewport.width}px`;
    canvas.style.height = `${viewport.height}px`;
    const context = canvas.getContext('2d');
    context.setTransform(dpr, 0, 0, dpr, 0, 0);
    context.clearRect(0, 0, viewport.width, viewport.height);

    const toScreen = (point) => ({
      x: point.x * view.scale + view.x,
      y: point.y * view.scale + view.y,
    });

    let tooDense = false;
    if (grid.enabled || terrainOverlayEnabled || selectedTerrainHexes.size) {
      const visible = getVisibleAxialRange(grid, view, viewport);
      const visibleCount = (visible.qMax - visible.qMin + 1) * (visible.rMax - visible.rMin + 1);
      tooDense = grid.sizePx * view.scale < MIN_SCREEN_GRID_SIZE || visibleCount > MAX_VISIBLE_HEXES;
      if (!tooDense) {
        const drawHexPath = (q, r) => {
          const center = axialToWorld(q, r, grid);
          if (!isPointInMap(center, imageWidth, imageHeight, grid.sizePx)) return false;
          const corners = getHexCorners(center, grid).map(toScreen);
          context.beginPath();
          context.moveTo(corners[0].x, corners[0].y);
          corners.slice(1).forEach((corner) => context.lineTo(corner.x, corner.y));
          context.closePath();
          return true;
        };

        if (terrainOverlayEnabled) {
          for (let q = visible.qMin; q <= visible.qMax; q += 1) {
            for (let r = visible.rMin; r <= visible.rMax; r += 1) {
              const assignment = terrainAssignments[hexKey(q, r)];
              const color = assignment && terrainPalette[assignment.terrainId];
              if (!color || !drawHexPath(q, r)) continue;
              const baseOpacity = clamp(terrainOverlayOpacity, 0.08, 0.8);
              context.globalAlpha = assignment.source === 'manual'
                ? Math.min(0.85, baseOpacity + 0.08)
                : baseOpacity;
              context.fillStyle = color;
              context.fill();
              if (assignment.source === 'auto' && assignment.confidence < 0.42) {
                context.globalAlpha = 0.95;
                context.strokeStyle = '#F59E0B';
                context.lineWidth = 2;
                context.setLineDash([4, 4]);
                context.stroke();
                context.setLineDash([]);
              }
            }
          }
        }

        if (grid.enabled) {
          context.beginPath();
          for (let q = visible.qMin; q <= visible.qMax; q += 1) {
            for (let r = visible.rMin; r <= visible.rMax; r += 1) {
              const center = axialToWorld(q, r, grid);
              if (
                center.x < -grid.sizePx || center.y < -grid.sizePx
                || center.x > imageWidth + grid.sizePx || center.y > imageHeight + grid.sizePx
              ) continue;
              const corners = getHexCorners(center, grid).map(toScreen);
              context.moveTo(corners[0].x, corners[0].y);
              corners.slice(1).forEach((corner) => context.lineTo(corner.x, corner.y));
              context.closePath();
            }
          }
          context.globalAlpha = grid.opacity;
          context.strokeStyle = grid.lineColor;
          context.lineWidth = grid.lineWidth;
          context.lineJoin = 'round';
          context.stroke();
        }

        if (selectedTerrainHexes.size) {
          context.globalAlpha = 1;
          context.strokeStyle = '#22D3EE';
          context.lineWidth = 3;
          selectedTerrainHexes.forEach((key) => {
            const [q, r] = key.split(',').map(Number);
            if (q < visible.qMin || q > visible.qMax || r < visible.rMin || r > visible.rMax) return;
            if (drawHexPath(q, r)) context.stroke();
          });
        }

        if (grid.enabled && grid.showLabels && grid.sizePx * view.scale >= 16) {
          context.globalAlpha = Math.min(1, grid.opacity + 0.25);
          context.fillStyle = grid.lineColor;
          context.textAlign = 'center';
          context.textBaseline = 'middle';
          context.font = `600 ${clamp(grid.sizePx * view.scale * 0.22, 9, 16)}px ui-monospace, monospace`;
          for (let q = visible.qMin; q <= visible.qMax; q += 1) {
            for (let r = visible.rMin; r <= visible.rMax; r += 1) {
              const center = axialToWorld(q, r, grid);
              if (center.x < 0 || center.y < 0 || center.x > imageWidth || center.y > imageHeight) continue;
              const screen = toScreen(center);
              context.fillText(formatHexId(q, r, mapBounds, grid.numbering), screen.x, screen.y);
            }
          }
        }
      }
    }
    setGridTooDense((current) => current === tooDense ? current : tooDense);

    if (networkOverlayEnabled) {
      Object.entries(networkSegments).forEach(([edgeKey, segment]) => {
        const [firstKey, secondKey] = edgeKey.split('|');
        if (!firstKey || !secondKey) return;
        const first = parseHexKey(firstKey);
        const second = parseHexKey(secondKey);
        const firstScreen = toScreen(axialToWorld(first.q, first.r, grid));
        const secondScreen = toScreen(axialToWorld(second.q, second.r, grid));
        if (
          Math.max(firstScreen.x, secondScreen.x) < -20
          || Math.max(firstScreen.y, secondScreen.y) < -20
          || Math.min(firstScreen.x, secondScreen.x) > viewport.width + 20
          || Math.min(firstScreen.y, secondScreen.y) > viewport.height + 20
        ) return;
        context.beginPath();
        context.moveTo(firstScreen.x, firstScreen.y);
        context.lineTo(secondScreen.x, secondScreen.y);
        context.globalAlpha = 0.95;
        context.strokeStyle = '#111827';
        context.lineWidth = Math.max(5, grid.sizePx * view.scale * 0.18);
        context.lineCap = 'round';
        context.stroke();
        context.strokeStyle = networkPalette[networkTypeKey(segment.catalog, segment.typeId)] || '#38BDF8';
        context.lineWidth = Math.max(2.5, grid.sizePx * view.scale * 0.09);
        context.setLineDash(segment.catalog === 'connection' ? [7, 4] : []);
        context.stroke();
        context.setLineDash([]);
      });
    }

    if (conditionOverlayEnabled) {
      Object.entries(conditionAssignments).forEach(([key, conditionIds]) => {
        const axial = parseHexKey(key);
        const screen = toScreen(axialToWorld(axial.q, axial.r, grid));
        if (screen.x < -20 || screen.y < -20 || screen.x > viewport.width + 20 || screen.y > viewport.height + 20) return;
        const color = conditionPalette[conditionIds[0]] || '#F97316';
        const radius = clamp(grid.sizePx * view.scale * 0.2, 6, 13);
        context.beginPath();
        context.arc(screen.x, screen.y, radius, 0, Math.PI * 2);
        context.globalAlpha = 0.95;
        context.fillStyle = color;
        context.fill();
        context.strokeStyle = '#FFFFFF';
        context.lineWidth = 2;
        context.stroke();
        context.fillStyle = '#FFFFFF';
        context.textAlign = 'center';
        context.textBaseline = 'middle';
        context.font = `800 ${clamp(radius * 1.25, 9, 15)}px sans-serif`;
        context.fillText('!', screen.x, screen.y + 0.5);
      });
    }

    if (routeVariants.length) {
      const ordered = [...routeVariants].sort((first, second) => (
        first.profileId === selectedRouteProfileId ? 1 : second.profileId === selectedRouteProfileId ? -1 : 0
      ));
      ordered.forEach((variant) => {
        if (variant.unavailable || variant.path.length < 2) return;
        const selected = variant.profileId === selectedRouteProfileId;
        context.beginPath();
        variant.path.forEach((key, index) => {
          const axial = parseHexKey(key);
          const screen = toScreen(axialToWorld(axial.q, axial.r, grid));
          if (index === 0) context.moveTo(screen.x, screen.y);
          else context.lineTo(screen.x, screen.y);
        });
        context.globalAlpha = selected ? 0.95 : 0.35;
        context.strokeStyle = routeProfileColors[variant.profileId] || '#FFFFFF';
        context.lineWidth = selected ? 5 : 2.5;
        context.lineCap = 'round';
        context.lineJoin = 'round';
        context.stroke();
      });
    }

    if (focusedRouteHexKey) {
      const axial = parseHexKey(focusedRouteHexKey);
      const center = axialToWorld(axial.q, axial.r, grid);
      const corners = getHexCorners(center, grid).map(toScreen);
      context.beginPath();
      context.moveTo(corners[0].x, corners[0].y);
      corners.slice(1).forEach((corner) => context.lineTo(corner.x, corner.y));
      context.closePath();
      context.globalAlpha = 0.35;
      context.fillStyle = '#F59E0B';
      context.fill();
      context.globalAlpha = 1;
      context.strokeStyle = '#FBBF24';
      context.lineWidth = 4;
      context.stroke();
    }

    const routeMarkers = routePoints ? [
      routePoints.startHexKey && { key: routePoints.startHexKey, label: 'D', color: '#22C55E' },
      ...(routePoints.waypointHexKeys || []).map((key, index) => ({ key, label: String(index + 1), color: '#38BDF8' })),
      routePoints.destinationHexKey && { key: routePoints.destinationHexKey, label: 'A', color: '#EF4444' },
    ].filter(Boolean) : [];
    routeMarkers.forEach((marker) => {
      const axial = parseHexKey(marker.key);
      const screen = toScreen(axialToWorld(axial.q, axial.r, grid));
      context.beginPath();
      context.arc(screen.x, screen.y, 10, 0, Math.PI * 2);
      context.globalAlpha = 1;
      context.fillStyle = marker.color;
      context.fill();
      context.strokeStyle = '#FFFFFF';
      context.lineWidth = 2;
      context.stroke();
      context.fillStyle = '#FFFFFF';
      context.textAlign = 'center';
      context.textBaseline = 'middle';
      context.font = '700 10px sans-serif';
      context.fillText(marker.label, screen.x, screen.y + 0.5);
    });

    avoidedRouteHexKeys.forEach((key) => {
      const axial = parseHexKey(key);
      const screen = toScreen(axialToWorld(axial.q, axial.r, grid));
      const radius = clamp(grid.sizePx * view.scale * 0.18, 7, 12);
      context.globalAlpha = 1;
      context.strokeStyle = '#FB7185';
      context.lineWidth = 3;
      context.lineCap = 'round';
      context.beginPath();
      context.moveTo(screen.x - radius, screen.y - radius);
      context.lineTo(screen.x + radius, screen.y + radius);
      context.moveTo(screen.x + radius, screen.y - radius);
      context.lineTo(screen.x - radius, screen.y + radius);
      context.stroke();
    });

    if (partyAnimation?.path?.length) {
      const path = partyAnimation.path;
      const position = clamp(partyAnimation.progress || 0, 0, 1) * Math.max(0, path.length - 1);
      const segmentIndex = Math.min(path.length - 2, Math.floor(position));
      const segmentProgress = position - segmentIndex;
      const firstAxial = parseHexKey(path[Math.max(0, segmentIndex)]);
      const secondAxial = parseHexKey(path[Math.min(path.length - 1, segmentIndex + 1)]);
      const first = toScreen(axialToWorld(firstAxial.q, firstAxial.r, grid));
      const second = toScreen(axialToWorld(secondAxial.q, secondAxial.r, grid));
      const point = {
        x: first.x + (second.x - first.x) * segmentProgress,
        y: first.y + (second.y - first.y) * segmentProgress,
      };
      const pulse = 17 + Math.sin((partyAnimation.progress || 0) * Math.PI * 18) * 2;
      context.globalAlpha = 0.28;
      context.fillStyle = partyAnimation.color || '#F59E0B';
      context.beginPath();
      context.arc(point.x, point.y, pulse, 0, Math.PI * 2);
      context.fill();
      context.globalAlpha = 1;
      context.fillStyle = '#111827';
      context.strokeStyle = partyAnimation.color || '#F59E0B';
      context.lineWidth = 4;
      context.beginPath();
      context.arc(point.x, point.y, 13, 0, Math.PI * 2);
      context.fill();
      context.stroke();
      const transportGlyph = partyAnimation.transportTags?.includes('road_vehicle')
        ? '🚗'
        : partyAnimation.transportTags?.includes('public_transit')
          ? '🚇'
          : partyAnimation.transportTags?.includes('mounted')
            ? '🐎'
            : null;
      if (transportGlyph) {
        context.font = '15px sans-serif';
        context.textAlign = 'center';
        context.textBaseline = 'middle';
        context.fillText(transportGlyph, point.x, point.y + 0.5);
      } else {
        context.fillStyle = '#FFFFFF';
        [-5, 0, 5].forEach((offset, index) => {
          context.beginPath();
          context.arc(point.x + offset, point.y - (index === 1 ? 3 : 1), index === 1 ? 3.2 : 2.5, 0, Math.PI * 2);
          context.fill();
        });
        context.beginPath();
        context.roundRect(point.x - 8, point.y + 3, 16, 5, 2.5);
        context.fill();
      }
      if (partyAnimation.name) {
        const label = partyAnimation.name.length > 26 ? `${partyAnimation.name.slice(0, 25)}…` : partyAnimation.name;
        context.font = '700 11px sans-serif';
        context.textAlign = 'center';
        const width = context.measureText(label).width + 12;
        context.fillStyle = 'rgba(17, 24, 39, 0.92)';
        context.fillRect(point.x - width / 2, point.y - 35, width, 17);
        context.fillStyle = '#FFFFFF';
        context.textBaseline = 'middle';
        context.fillText(label, point.x, point.y - 26.5);
      }
    }

    if (pinsVisible && pins.length) {
      const showLabels = pins.length <= 20 || view.scale >= 0.35;
      pins.forEach((pin) => {
        const screen = toScreen(pinToMapPoint(pin, imageWidth, imageHeight));
        if (
          screen.x < -80 || screen.y < -40
          || screen.x > viewport.width + 80 || screen.y > viewport.height + 40
        ) return;
        const color = resolvePinColor(pin);
        const muted = ['resolved', 'destroyed', 'inaccessible'].includes(pin.state);
        context.globalAlpha = muted ? 0.55 : 1;
        context.fillStyle = color;
        context.strokeStyle = '#FFFFFF';
        context.lineWidth = 2;
        context.beginPath();
        context.arc(screen.x, screen.y - 7, 8, 0, Math.PI * 2);
        context.fill();
        context.stroke();
        context.beginPath();
        context.moveTo(screen.x - 5, screen.y - 1);
        context.lineTo(screen.x, screen.y + 8);
        context.lineTo(screen.x + 5, screen.y - 1);
        context.closePath();
        context.fill();
        context.beginPath();
        context.arc(screen.x, screen.y - 7, 2.5, 0, Math.PI * 2);
        context.fillStyle = '#FFFFFF';
        context.fill();

        if (!pin.discovered) {
          context.globalAlpha = 0.9;
          context.strokeStyle = '#FBBF24';
          context.lineWidth = 1.5;
          context.setLineDash([3, 3]);
          context.beginPath();
          context.arc(screen.x, screen.y - 7, 12, 0, Math.PI * 2);
          context.stroke();
          context.setLineDash([]);
        }

        if (showLabels && pin.title) {
          const label = pin.title.length > 28 ? `${pin.title.slice(0, 27)}…` : pin.title;
          context.globalAlpha = 0.92;
          context.font = '600 11px sans-serif';
          context.textAlign = 'left';
          context.textBaseline = 'middle';
          const labelWidth = context.measureText(label).width;
          context.fillStyle = '#111827';
          context.fillRect(screen.x + 12, screen.y - 18, labelWidth + 12, 19);
          context.fillStyle = '#FFFFFF';
          context.fillText(label, screen.x + 18, screen.y - 8.5);
        }
      });
      context.globalAlpha = 1;
    }

    if (measurementPoints.length) {
      const screenPoints = measurementPoints.map(toScreen);
      context.globalAlpha = 1;
      context.strokeStyle = '#F59E0B';
      context.fillStyle = '#F59E0B';
      context.lineWidth = 2;
      context.setLineDash([6, 4]);
      if (screenPoints.length === 2) {
        context.beginPath();
        context.moveTo(screenPoints[0].x, screenPoints[0].y);
        context.lineTo(screenPoints[1].x, screenPoints[1].y);
        context.stroke();
      }
      context.setLineDash([]);
      screenPoints.forEach((point, index) => {
        context.beginPath();
        context.arc(point.x, point.y, 7, 0, Math.PI * 2);
        context.fill();
        context.fillStyle = '#111827';
        context.font = '700 10px sans-serif';
        context.fillText(String(index + 1), point.x, point.y + 0.5);
        context.fillStyle = '#F59E0B';
      });
    }
    context.globalAlpha = 1;
  }, [avoidedRouteHexKeys, conditionAssignments, conditionOverlayEnabled, conditionPalette, focusedRouteHexKey, grid, imageHeight, imageWidth, mapBounds, measurementPoints, networkOverlayEnabled, networkPalette, networkSegments, partyAnimation, pins, pinsVisible, routePoints, routeProfileColors, routeVariants, selectedRouteProfileId, selectedTerrainHexes, terrainAssignments, terrainOverlayEnabled, terrainOverlayOpacity, terrainPalette, view, viewport]);

  useEffect(() => {
    if (interactionMode !== 'terrain_paint' || !hoveredTerrainAxial) {
      prepareOverlayContext(terrainPreviewCanvasRef.current, true);
      return;
    }
    const previewCells = axialDisk(hoveredTerrainAxial, brushRadius);
    drawTerrainCells(terrainPreviewCanvasRef.current, previewCells, {
      clear: true,
      previewCenter: hoveredTerrainAxial,
    });
  }, [brushRadius, drawTerrainCells, hoveredTerrainAxial, interactionMode, prepareOverlayContext]);

  useEffect(() => {
    const pendingKeys = terrainStrokeRef.current?.pendingKeys;
    drawTerrainCells(
      terrainStrokeCanvasRef.current,
      pendingKeys ? [...pendingKeys] : [],
      { clear: true },
    );
  }, [drawTerrainCells]);

  const screenToWorld = useCallback((clientX, clientY) => {
    const rect = containerRef.current.getBoundingClientRect();
    return {
      x: (clientX - rect.left - view.x) / view.scale,
      y: (clientY - rect.top - view.y) / view.scale,
    };
  }, [view]);

  const handlePointerDown = (event) => {
    containerRef.current?.focus({ preventScroll: true });
    if (event.button !== 0) return;
    if (interactionMode.startsWith('route_')) {
      const point = screenToWorld(event.clientX, event.clientY);
      if (point.x < 0 || point.y < 0 || point.x > imageWidth || point.y > imageHeight) return;
      const pinHit = pinsVisible ? pins.find((pin) => {
        const pinPoint = pinToMapPoint(pin, imageWidth, imageHeight);
        return Math.hypot(pinPoint.x - point.x, pinPoint.y - point.y) * view.scale <= 20;
      }) : null;
      const pickedPoint = pinHit ? pinToMapPoint(pinHit, imageWidth, imageHeight) : point;
      const axial = worldToAxial(pickedPoint, grid);
      if (!isPointInMap(axialToWorld(axial.q, axial.r, grid), imageWidth, imageHeight)) return;
      onRouteHexPick?.(interactionMode, hexKey(axial.q, axial.r), pinHit?.id || null);
      return;
    }
    if (interactionMode.startsWith('network_')) {
      const point = screenToWorld(event.clientX, event.clientY);
      if (point.x < 0 || point.y < 0 || point.x > imageWidth || point.y > imageHeight) return;
      const axial = worldToAxial(point, grid);
      const key = hexKey(axial.q, axial.r);
      event.currentTarget.setPointerCapture(event.pointerId);
      featureStrokeRef.current = { pointerId: event.pointerId, lastKey: key };
      onNetworkStrokeStart?.();
      return;
    }
    if (interactionMode.startsWith('condition_')) {
      const point = screenToWorld(event.clientX, event.clientY);
      if (point.x < 0 || point.y < 0 || point.x > imageWidth || point.y > imageHeight) return;
      const axial = worldToAxial(point, grid);
      onConditionPaint?.(hexKey(axial.q, axial.r), interactionMode === 'condition_erase');
      return;
    }
    if (interactionMode.startsWith('terrain_')) {
      const point = screenToWorld(event.clientX, event.clientY);
      if (point.x < 0 || point.y < 0 || point.x > imageWidth || point.y > imageHeight) return;
      const axial = worldToAxial(point, grid);
      const key = hexKey(axial.q, axial.r);
      setHoveredTerrainAxial(axial);
      if (interactionMode === 'terrain_sample') {
        onTerrainSample?.(key, axial);
      } else if (interactionMode === 'terrain_select') {
        onToggleTerrainHex?.(key);
      } else if (interactionMode === 'terrain_paint') {
        event.currentTarget.setPointerCapture(event.pointerId);
        const keys = axialDisk(axial, brushRadius)
          .filter((cell) => isPointInMap(axialToWorld(cell.q, cell.r, grid), imageWidth, imageHeight))
          .map((cell) => hexKey(cell.q, cell.r));
        terrainStrokeRef.current = {
          pointerId: event.pointerId,
          lastKey: key,
          pendingKeys: new Set(keys),
        };
        prepareOverlayContext(terrainStrokeCanvasRef.current, true);
        drawTerrainCells(terrainStrokeCanvasRef.current, keys);
        onTerrainStrokeStart?.();
      }
      return;
    }
    if (interactionMode !== 'navigate') {
      const point = screenToWorld(event.clientX, event.clientY);
      if (point.x < 0 || point.y < 0 || point.x > imageWidth || point.y > imageHeight) return;
      if (measurementPoints.length === 0) {
        setMeasurementPoints([point]);
      } else {
        const points = [measurementPoints[0], point];
        setMeasurementPoints(points);
        if (interactionMode === 'grid') {
          const result = calibrateGridFromTwoPoints(points[0], points[1], grid.orientation);
          if (result) onGridCalibrated(result);
        } else if (interactionMode === 'scale') {
          onScaleSegmentMeasured({
            points,
            pixelLength: Math.hypot(points[1].x - points[0].x, points[1].y - points[0].y),
          });
        }
        onInteractionComplete();
      }
      return;
    }
    event.currentTarget.setPointerCapture(event.pointerId);
    dragRef.current = { pointerId: event.pointerId, clientX: event.clientX, clientY: event.clientY, view };
  };

  const handlePointerMove = (event) => {
    if (interactionMode === 'terrain_paint') {
      const point = screenToWorld(event.clientX, event.clientY);
      const nextHovered = point.x >= 0 && point.y >= 0 && point.x <= imageWidth && point.y <= imageHeight
        ? worldToAxial(point, grid)
        : null;
      setHoveredTerrainAxial((current) => (
        current?.q === nextHovered?.q && current?.r === nextHovered?.r ? current : nextHovered
      ));
    }
    const featureStroke = featureStrokeRef.current;
    if (featureStroke?.pointerId === event.pointerId) {
      const point = screenToWorld(event.clientX, event.clientY);
      if (point.x < 0 || point.y < 0 || point.x > imageWidth || point.y > imageHeight) return;
      const axial = worldToAxial(point, grid);
      const key = hexKey(axial.q, axial.r);
      if (featureStroke.lastKey === key) return;
      const edges = networkEdgesBetween(featureStroke.lastKey, key);
      featureStroke.lastKey = key;
      onNetworkPaint?.(edges, interactionMode === 'network_erase');
      return;
    }
    const stroke = terrainStrokeRef.current;
    if (stroke?.pointerId === event.pointerId) {
      const point = screenToWorld(event.clientX, event.clientY);
      if (point.x < 0 || point.y < 0 || point.x > imageWidth || point.y > imageHeight) return;
      const axial = worldToAxial(point, grid);
      const key = hexKey(axial.q, axial.r);
      if (stroke.lastKey === key) return;
      const previousAxial = parseHexKey(stroke.lastKey);
      stroke.lastKey = key;
      const keys = [...new Set(axialLine(previousAxial, axial)
        .flatMap((cell) => axialDisk(cell, brushRadius))
        .filter((cell) => isPointInMap(axialToWorld(cell.q, cell.r, grid), imageWidth, imageHeight))
        .map((cell) => hexKey(cell.q, cell.r)))];
      const freshKeys = keys.filter((paintedKey) => !stroke.pendingKeys.has(paintedKey));
      freshKeys.forEach((paintedKey) => stroke.pendingKeys.add(paintedKey));
      drawTerrainCells(terrainStrokeCanvasRef.current, freshKeys);
      return;
    }
    const drag = dragRef.current;
    if (!drag || drag.pointerId !== event.pointerId) return;
    setView({
      ...drag.view,
      x: drag.view.x + event.clientX - drag.clientX,
      y: drag.view.y + event.clientY - drag.clientY,
    });
  };

  const handlePointerUp = (event) => {
    if (featureStrokeRef.current?.pointerId === event.pointerId) {
      featureStrokeRef.current = null;
      onNetworkStrokeEnd?.();
    }
    if (terrainStrokeRef.current?.pointerId === event.pointerId) {
      const keys = [...terrainStrokeRef.current.pendingKeys];
      terrainStrokeRef.current = null;
      onTerrainPaint?.(keys);
      onTerrainStrokeEnd?.();
      window.requestAnimationFrame(() => {
        prepareOverlayContext(terrainStrokeCanvasRef.current, true);
      });
    }
    if (dragRef.current?.pointerId === event.pointerId) dragRef.current = null;
  };

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return undefined;
    const handleWheel = (event) => {
      event.preventDefault();
      if (event.altKey) {
        const delta = event.deltaY > 0 ? -1 : 1;
        onGridChange({ ...grid, sizePx: clamp(Math.round(grid.sizePx + delta), 8, 500) });
        return;
      }
      const rect = container.getBoundingClientRect();
      const mouseX = event.clientX - rect.left;
      const mouseY = event.clientY - rect.top;
      setView((current) => {
        const minimum = Math.min(rect.width / imageWidth, rect.height / imageHeight) * 0.2;
        const maximum = Math.max(8, minimum * 40);
        const newScale = clamp(current.scale * Math.exp(-event.deltaY * 0.0015), minimum, maximum);
        const worldX = (mouseX - current.x) / current.scale;
        const worldY = (mouseY - current.y) / current.scale;
        return { scale: newScale, x: mouseX - worldX * newScale, y: mouseY - worldY * newScale };
      });
    };
    container.addEventListener('wheel', handleWheel, { passive: false });
    return () => container.removeEventListener('wheel', handleWheel);
  }, [grid, imageHeight, imageWidth, onGridChange]);

  const handleKeyDown = (event) => {
    if (event.key.toLowerCase() === 'f') {
      event.preventDefault();
      fitView(imageWidth, imageHeight, false);
      return;
    }
    if (event.key.toLowerCase() === 'g') {
      event.preventDefault();
      onGridChange({ ...grid, enabled: !grid.enabled });
      return;
    }
    const arrows = { ArrowLeft: [-1, 0], ArrowRight: [1, 0], ArrowUp: [0, -1], ArrowDown: [0, 1] };
    if (arrows[event.key]) {
      event.preventDefault();
      const [dx, dy] = arrows[event.key];
      const multiplier = event.shiftKey ? 10 : 1;
      onGridChange({
        ...grid,
        offsetX: grid.offsetX + dx * multiplier,
        offsetY: grid.offsetY + dy * multiplier,
      });
    }
  };

  const zoomAtCenter = (factor) => {
    setView((current) => {
      const centerX = viewport.width / 2;
      const centerY = viewport.height / 2;
      const newScale = clamp(current.scale * factor, 0.01, 20);
      const worldX = (centerX - current.x) / current.scale;
      const worldY = (centerY - current.y) / current.scale;
      return { scale: newScale, x: centerX - worldX * newScale, y: centerY - worldY * newScale };
    });
  };

  const scaleBar = getScaleBar(calibration.scale.metersPerPixel, view.scale);
  const interactionMessage = interactionMode === 'grid'
    ? (measurementPoints.length ? text.gridPoint2 : text.gridPoint1)
    : interactionMode === 'scale'
      ? (measurementPoints.length ? text.scalePoint2 : text.scalePoint1)
      : interactionMode === 'terrain_sample'
        ? text.sampleTerrain
        : interactionMode === 'terrain_paint'
          ? text.paintTerrain
          : interactionMode === 'terrain_select'
            ? text.selectTerrain
            : interactionMode === 'route_start'
              ? text.routeStart
              : interactionMode === 'route_destination'
                ? text.routeDestination
                : interactionMode === 'route_waypoint'
                  ? text.routeWaypoint
                  : interactionMode === 'route_edit'
                    ? text.routeEdit
                  : interactionMode === 'network_draw'
                    ? text.drawNetwork
                    : interactionMode === 'network_erase'
                      ? text.eraseNetwork
                      : interactionMode === 'condition_paint'
                        ? text.paintCondition
                        : interactionMode === 'condition_erase'
                          ? text.eraseCondition
      : null;

  return (
    <div
      ref={containerRef}
      tabIndex={0}
      className={`relative h-full ${immersive ? 'min-h-0' : 'min-h-[540px]'} touch-none overflow-hidden bg-[#151719] outline-none focus-visible:ring-2 focus-visible:ring-primary ${interactionMode === 'navigate' ? 'cursor-grab active:cursor-grabbing' : 'cursor-crosshair'}`}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
      onPointerCancel={handlePointerUp}
      onLostPointerCapture={handlePointerUp}
      onPointerLeave={() => {
        if (!terrainStrokeRef.current) setHoveredTerrainAxial(null);
      }}
      onKeyDown={handleKeyDown}
      data-testid="travel-map-canvas"
      aria-label={`${map.name} — ${text.gridHint}`}
    >
      {!imageError && (
        <img
          src={map.image_url}
          alt={map.name}
          draggable="false"
          className="pointer-events-none absolute left-0 top-0 max-w-none select-none will-change-transform"
          style={{
            width: imageWidth,
            height: imageHeight,
            transformOrigin: '0 0',
            transform: `translate3d(${view.x}px, ${view.y}px, 0) scale(${view.scale})`,
          }}
          onLoad={(event) => {
            const width = event.currentTarget.naturalWidth;
            const height = event.currentTarget.naturalHeight;
            if (width && height && (width !== imageWidth || height !== imageHeight)) {
              onImageDimensions({ width, height });
              fitView(width, height);
            }
          }}
          onError={() => setImageError(true)}
        />
      )}
      {imageError && (
        <div className="pointer-events-none absolute inset-0 flex items-center justify-center text-sm text-white/70">{text.imageError}</div>
      )}
      <canvas ref={canvasRef} data-testid="travel-map-base-layer" className="pointer-events-none absolute inset-0" />
      <canvas ref={terrainStrokeCanvasRef} data-testid="travel-map-terrain-stroke-layer" className="pointer-events-none absolute inset-0" />
      <canvas ref={terrainPreviewCanvasRef} data-testid="travel-map-terrain-preview-layer" className="pointer-events-none absolute inset-0" />

      <div onPointerDown={(event) => event.stopPropagation()} className="absolute right-3 top-3 flex flex-col gap-1 rounded-lg border border-white/10 bg-black/65 p-1 shadow-lg backdrop-blur">
        <Button variant="ghost" size="icon" className="h-8 w-8 text-white hover:bg-white/15 hover:text-white" onClick={() => zoomAtCenter(1.25)} title={text.zoomIn}>
          <Plus />
        </Button>
        <Button variant="ghost" size="icon" className="h-8 w-8 text-white hover:bg-white/15 hover:text-white" onClick={() => zoomAtCenter(0.8)} title={text.zoomOut}>
          <Minus />
        </Button>
        <Button variant="ghost" size="icon" className="h-8 w-8 text-white hover:bg-white/15 hover:text-white" onClick={() => fitView(imageWidth, imageHeight, false)} title={text.fit}>
          <LocateFixed />
        </Button>
        <Button variant="ghost" size="icon" className="h-8 w-8 text-white hover:bg-white/15 hover:text-white" onClick={() => onGridChange({ ...grid, enabled: !grid.enabled })} title={grid.enabled ? text.hideGrid : text.showGrid}>
          {grid.enabled ? <Eye /> : <EyeOff />}
        </Button>
        <Button
          variant="ghost"
          size="icon"
          className="relative h-8 w-8 text-white hover:bg-white/15 hover:text-white"
          onClick={() => onPinsVisibleChange?.(!pinsVisible)}
          title={pins.length ? `${pinsVisible ? text.hidePins : text.showPins} · ${pins.length}` : text.noPins}
          aria-label={pinsVisible ? text.hidePins : text.showPins}
        >
          {pinsVisible ? <MapPin /> : <MapPinOff />}
          {pins.length > 0 && <span className="absolute -right-1 -top-1 min-w-4 rounded-full bg-primary px-1 text-[9px] font-bold leading-4 text-primary-foreground">{pins.length > 99 ? '99+' : pins.length}</span>}
        </Button>
      </div>

      {!immersive && (
        <div className="pointer-events-none absolute left-3 top-3 rounded-lg border border-white/10 bg-black/65 px-3 py-2 text-white shadow-lg backdrop-blur">
          <p className="text-sm font-semibold">{map.name}</p>
          <p className="text-[10px] text-white/65">{imageWidth.toLocaleString()} × {imageHeight.toLocaleString()} px</p>
        </div>
      )}

      {interactionMessage && (
        <div className="pointer-events-none absolute left-1/2 top-4 flex -translate-x-1/2 items-center gap-2 rounded-full border border-amber-400/40 bg-black/80 px-4 py-2 text-sm font-medium text-white shadow-xl backdrop-blur">
          <MousePointer2 className="h-4 w-4 text-amber-400" />
          {interactionMessage}
        </div>
      )}

      {gridTooDense && grid.enabled && (
        <div className="pointer-events-none absolute bottom-16 left-3 rounded bg-black/65 px-2 py-1 text-[11px] text-white/75">{text.dense}</div>
      )}

      {!immersive && (
        <div className="pointer-events-none absolute bottom-3 left-3 rounded-lg border border-white/10 bg-black/65 px-3 py-2 text-white shadow-lg backdrop-blur">
          {scaleBar ? (
            <>
              <div className="border-x border-t border-white" style={{ width: Math.max(35, scaleBar.screenPixels), height: 7 }} />
              <p className="mt-1 text-center text-[11px] font-medium">{formatDistance(scaleBar.meters, distanceUnit, language)}</p>
            </>
          ) : <p className="text-[11px] text-white/70">{text.gridHint}</p>}
        </div>
      )}
    </div>
  );
}

export default TravelMapCanvas;
